import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, formatINR } from "../lib/api";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const active = params.get("category") || "";

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  // Determine which parent is active for showing sub-filters
  const activeCat = useMemo(() => categories.find((c) => c.slug === active), [categories, active]);
  const parentSlug = activeCat?.parent_slug || (activeCat && !activeCat.parent_slug ? activeCat.slug : "");
  const topCats = useMemo(
    () => categories.filter((c) => !c.parent_slug && c.slug !== "chocolates"),
    [categories],
  );
  const subCats = useMemo(
    () => (parentSlug ? categories.filter((c) => c.parent_slug === parentSlug) : []),
    [categories, parentSlug],
  );

  // Descendant slugs: if user picks a top-level with children, include all its children's products too
  const descendantSlugs = useMemo(() => {
    if (!activeCat) return [];
    if (activeCat.parent_slug) return [activeCat.slug]; // leaf
    const kids = categories.filter((c) => c.parent_slug === activeCat.slug).map((c) => c.slug);
    return [activeCat.slug, ...kids];
  }, [activeCat, categories]);

  useEffect(() => {
    setLoading(true);
    // If a parent with children is selected, fetch all products then filter client-side by descendant slugs.
    // (Keeps backend endpoint simple.) All shop results are hampers only.
    const baseParams = { product_type: "hamper" };
    if (descendantSlugs.length > 1) {
      api
        .get("/products", { params: baseParams })
        .then(({ data }) => setProducts(data.filter((p) => descendantSlugs.includes(p.category))))
        .finally(() => setLoading(false));
    } else {
      api
        .get("/products", { params: { ...baseParams, ...(active ? { category: active } : {}) } })
        .then(({ data }) => setProducts(data))
        .finally(() => setLoading(false));
    }
  }, [active, descendantSlugs]);

  const setCategory = (slug) => {
    if (!slug) params.delete("category");
    else params.set("category", slug);
    setParams(params);
  };

  return (
    <div className="App">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10 lg:py-20" data-testid="shop-page">
        <p className="label-tiny mb-3">Collection</p>
        <h1 className="font-serif text-4xl text-[#2a2928] sm:text-5xl lg:text-6xl" data-testid="shop-heading">
          The hamper library.
        </h1>
        <p className="mt-4 max-w-lg text-[#7a7571]">
          Browse our handcrafted stuffed balloon hampers. Every piece is made to order.
        </p>

        {/* Top-level filter chips */}
        <div className="mt-10 flex flex-wrap gap-2 border-t border-[#e8e2da] pt-8" data-testid="shop-filters">
          <button
            onClick={() => setCategory("")}
            data-testid="filter-all"
            className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
              !active
                ? "border-[#d08c8c] bg-[#d08c8c] text-white"
                : "border-[#e8e2da] bg-white text-[#2a2928] hover:border-[#d08c8c]"
            }`}
          >
            All
          </button>
          {topCats.map((c) => {
            const isActive = active === c.slug || activeCat?.parent_slug === c.slug;
            return (
              <button
                key={c.slug}
                data-testid={`filter-${c.slug}`}
                onClick={() => setCategory(c.slug)}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "border-[#d08c8c] bg-[#d08c8c] text-white"
                    : "border-[#e8e2da] bg-white text-[#2a2928] hover:border-[#d08c8c]"
                }`}
              >
                {c.name}
              </button>
            );
          })}
        </div>

        {/* Sub-filter chips (only when a parent with children is active) */}
        {subCats.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2 rounded-2xl bg-[#faf1ee] p-3" data-testid="shop-subfilters">
            <span className="mr-1 self-center text-xs uppercase tracking-[0.2em] text-[#7a7571]">
              Refine
            </span>
            <button
              onClick={() => setCategory(parentSlug)}
              data-testid={`subfilter-all-${parentSlug}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                active === parentSlug
                  ? "border-[#d08c8c] bg-[#d08c8c] text-white"
                  : "border-transparent bg-white text-[#2a2928] hover:border-[#d08c8c]"
              }`}
            >
              All
            </button>
            {subCats.map((s) => (
              <button
                key={s.slug}
                data-testid={`subfilter-${s.slug}`}
                onClick={() => setCategory(s.slug)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  active === s.slug
                    ? "border-[#d08c8c] bg-[#d08c8c] text-white"
                    : "border-transparent bg-white text-[#2a2928] hover:border-[#d08c8c]"
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="shop-product-grid">
          {loading && [...Array(6)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[4/5] rounded-3xl bg-[#f0e9e1]" />
              <div className="mt-4 h-4 w-2/3 rounded bg-[#f0e9e1]" />
              <div className="mt-2 h-3 w-1/2 rounded bg-[#f0e9e1]" />
            </div>
          ))}
          {!loading && products.length === 0 && (
            <div className="col-span-full rounded-3xl border border-dashed border-[#e8e2da] py-20 text-center text-[#7a7571]" data-testid="shop-empty">
              No hampers in this category yet. Check back soon.
            </div>
          )}
          {!loading && products.map((p) => (
            <Link
              to={`/product/${p.id}`}
              key={p.id}
              data-testid={`shop-product-${p.id}`}
              className="group card-lift"
            >
              <div className="overflow-hidden rounded-3xl border border-[#e8e2da] bg-white">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={p.image_base64 || p.image_url}
                    alt={p.name}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>
              <div className="mt-4 flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-[#7a7571]">
                    {categories.find((c) => c.slug === p.category)?.name || p.category}
                  </p>
                  <h3 className="mt-1 font-serif text-xl text-[#2a2928]">{p.name}</h3>
                </div>
                <div className="whitespace-nowrap font-serif text-lg text-[#2a2928]">
                  {formatINR(p.price)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
